"use client";

import { useRef, useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

// Floating geometry for about page
function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);
  const mesh3Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.012;
    }
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.06;
      mesh1Ref.current.rotation.z = t * 0.03;
      mesh1Ref.current.position.y = Math.sin(t * 0.2) * 0.5;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.05;
      mesh2Ref.current.position.y = Math.sin(t * 0.25 + 1) * 0.4;
    }
    if (mesh3Ref.current) {
      mesh3Ref.current.rotation.x = t * 0.04;
      mesh3Ref.current.rotation.y = t * 0.025;
      mesh3Ref.current.position.y = Math.sin(t * 0.22 + 2) * 0.3;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={mesh1Ref} position={[3, 0.5, -1]}>
        <dodecahedronGeometry args={[2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.3} transparent />
      </mesh>

      <mesh ref={mesh2Ref} position={[-2.5, -0.5, 0]}>
        <icosahedronGeometry args={[1.6, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.25} transparent />
      </mesh>

      <mesh ref={mesh3Ref} position={[0.5, -2, -2]}>
        <octahedronGeometry args={[1.3, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.2} transparent />
      </mesh>
    </group>
  );
}

// Dot grid
function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);

  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.z = state.clock.elapsedTime * 0.006;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

// Typewriter hook
function useTypewriter(text: string, speed: number = 50, delay: number = 0) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, delay]);

  return { displayedText, isComplete };
}

// Scroll-animated section
function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      }}
    >
      {children}
    </div>
  );
}

const values = [
  {
    title: "Software Excellence",
    description: "We build production-grade software designed for scale, maintainability, and long-term ownership inside complex enterprise environments.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
      </svg>
    ),
  },
  {
    title: "Velocity",
    description: "We move quickly without sacrificing quality by embedding with client teams, reducing handoffs, and delivering in short, high-impact iterations.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    title: "Business Alignment",
    description: "We work closely with stakeholders across product, engineering, and operations to ensure technology decisions align with real business outcomes.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
      </svg>
    ),
  },
  {
    title: "Innovation",
    description: "We apply modern architectures, AI-assisted workflows, and platform thinking where they create real leverage—not innovation theater.",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
  },
];

const process = [
  {
    number: "01",
    title: "Design & Discovery",
    description: "We begin with workshops, user research, and rapid prototyping, aligning technical decisions with organizational constraints, timelines, and stakeholders.",
  },
  {
    number: "02",
    title: "Iterative Development",
    description: "We deliver in short, focused cycles that support ongoing feedback from product owners, engineering teams, and business leaders.",
  },
  {
    number: "03",
    title: "CI/CD Integration",
    description: "Our DevOps practices support enterprise-grade reliability, security, and repeatability across environments.",
  },
];

const engagementModels = [
  {
    title: "Project Outsourcing",
    description: "End-to-end project delivery with our dedicated team managing the full software development lifecycle.",
  },
  {
    title: "Team Augmentation",
    description: "Embedded engineers who integrate directly with your team, tools, and delivery cadence, often for multi-year partnerships.",
  },
  {
    title: "Retainer Model",
    description: "Ongoing partnership with dedicated resources available for continuous development and support.",
  },
  {
    title: "POCs / Innovation Labs",
    description: "Rapid prototyping and proof-of-concept development to validate ideas before full investment.",
  },
];

export default function AboutPage() {
  const [glowPos, setGlowPos] = useState({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });

  // Typewriter effect for hero headline
  const { displayedText: heroText, isComplete: heroComplete } = useTypewriter(
    "Innovators and problem-solvers",
    45,
    300
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      targetPos.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      targetPos.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    let animationId: number;
    const currentPos = { x: 0, y: 0 };

    const animate = () => {
      currentPos.x += (targetPos.current.x - currentPos.x) * 0.03;
      currentPos.y += (targetPos.current.y - currentPos.y) * 0.03;
      setGlowPos({ x: currentPos.x, y: currentPos.y });
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden">
      {/* Three.js Canvas Background */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10">
        <Navigation />

        <main id="main-content">

        {/* Hero */}
        <section className="pt-32 pb-8 md:pb-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl">
              <div className="min-h-[2.4em]">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight mb-6 lg:whitespace-nowrap">
                  {heroText.includes("problem-solvers") ? (
                    <>
                      {heroText.slice(0, heroText.indexOf("problem-solvers"))}
                      <span className="text-[#ff6900]">{heroText.slice(heroText.indexOf("problem-solvers"))}</span>
                    </>
                  ) : (
                    heroText
                  )}
                  <span
                    className={`inline-block w-[3px] h-[0.9em] bg-[#ff6900] ml-1 align-middle ${heroComplete ? 'opacity-0' : 'animate-pulse'}`}
                  />
                </h1>
              </div>
              <p
                className="text-xl text-[#1a1a1a]/70 leading-relaxed transition-all duration-500"
                style={{
                  opacity: heroComplete ? 1 : 0,
                  transform: heroComplete ? 'translateY(0)' : 'translateY(10px)',
                }}
              >
                Founded in 2006 and based in Boulder, Colorado, we partner with enterprises to design, build, and scale digital platforms that power real-world operations and customer experiences.
              </p>
            </div>
          </div>
        </section>

        {/* Team Images */}
        <section
          className="pt-4 pb-8 md:pt-8 md:pb-16 px-6 transition-all duration-700"
          style={{
            opacity: heroComplete ? 1 : 0,
            transform: heroComplete ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
              <AnimatedSection delay={0} className="md:col-span-3">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="/images/about/team-1.jpg"
                    alt="Mapleton Hill Developers"
                    className="w-full h-full object-cover animate-float"
                  />
                </div>
              </AnimatedSection>
              <AnimatedSection delay={150} className="md:col-span-2">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="/images/about/team-2.jpg"
                    alt="Mapleton Hill Engineers"
                    className="w-full h-full object-cover animate-float-delayed"
                  />
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Our Difference / Values */}
        <section
          className="pt-8 pb-16 md:pt-12 md:pb-24 px-6 transition-all duration-700"
          style={{
            opacity: heroComplete ? 1 : 0,
            transform: heroComplete ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm text-[#ff6900] font-medium mb-2 block">Our Difference</span>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                What sets us apart
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((value, index) => (
                <AnimatedSection key={value.title} delay={index * 100}>
                  <div className="group bg-white rounded-2xl p-8 border border-[#1a1a1a]/5 hover:border-[#ff6900]/20 hover:shadow-lg hover:shadow-[#ff6900]/5 transition-all duration-300">
                    <div className="w-12 h-12 text-[#ff6900] mb-6 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-[#ff6900] transition-colors">
                      {value.title}
                    </h3>
                    <p className="text-[#1a1a1a]/70 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section
          className="py-16 md:py-24 px-6 bg-white/50 transition-opacity duration-700"
          style={{ opacity: heroComplete ? 1 : 0 }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm text-[#ff6900] font-medium mb-2 block">Our Process</span>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                A blueprint for success
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {process.map((step, index) => (
                <AnimatedSection key={step.number} delay={index * 150}>
                  <div className="relative group cursor-default">
                    <div className="text-6xl font-bold text-[#ff6900]/10 group-hover:text-[#ff6900] transition-colors duration-300 mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">
                      {step.title}
                    </h3>
                    <p className="text-[#1a1a1a]/70 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement Models */}
        <section
          className="py-16 md:py-24 px-6 transition-opacity duration-700"
          style={{ opacity: heroComplete ? 1 : 0 }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm text-[#ff6900] font-medium mb-2 block">How We Work</span>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Engagement models
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {engagementModels.map((model, index) => (
                <AnimatedSection key={model.title} delay={index * 100}>
                  <div className="group bg-white rounded-2xl p-6 border border-[#1a1a1a]/5 hover:border-[#ff6900]/20 hover:shadow-lg transition-all duration-300 h-full">
                    <h3 className="text-lg font-semibold mb-2 group-hover:text-[#ff6900] transition-colors">
                      {model.title}
                    </h3>
                    <p className="text-sm text-[#1a1a1a]/70 leading-relaxed">
                      {model.description}
                    </p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="py-24 px-6 bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white transition-opacity duration-700"
          style={{ opacity: heroComplete ? 1 : 0 }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
              Ready to work together?
            </h2>
            <p className="text-xl text-white/70 mb-10">
              Let&apos;s discuss how we can help transform your ideas into digital realities.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#1a1a1a] font-medium hover:bg-white/90 transition-colors"
            >
              Get in touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>
        </main>

        <div
          className="transition-opacity duration-700"
          style={{ opacity: heroComplete ? 1 : 0 }}
        >
          <Footer />
        </div>
      </div>
    </div>
  );
}
